import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Check, Coffee, Leaf, Truck, Handshake, Phone } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { MaskLine, FadeUp } from "../components/Reveal";
import { SITE, waLink } from "../config/site";
import { WhatsAppIcon } from "../components/DealerSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = {
  storefront:
    "https://static.prod-images.emergentagent.com/jobs/2e8907c4-4100-4573-95dc-8f4deef5c7ce/images/3bf5109c47a055d82c25fad9ca8eb162d740fd052b628d37677268729133f5b5.jpeg",
  distribution:
    "https://static.prod-images.emergentagent.com/jobs/2e8907c4-4100-4573-95dc-8f4deef5c7ce/images/5466e0d81ba79d0b1737ea1ac7d9cb0b28f2a6c465cc6f1bcf89638128e1f6dd.jpeg",
};

const CATEGORIES = [
  { icon: Coffee, title: "Filter Coffee Powder", text: "Our signature slow-roasted blend, ground for the traditional filter." },
  { icon: Leaf, title: "Premium Tea Powder", text: "Selected leaf tea that brews into a warm, full-flavoured cup." },
  { icon: Leaf, title: "Assam Tea Powder", text: "Strong Assam leaves with deep colour and malty strength." },
  { icon: Truck, title: "Wholesale & Distribution", text: "Coffee and tea product distribution through our supply network." },
];

const HIGHLIGHTS = [
  { icon: <span className="font-display text-2xl text-gold">₹2L</span>, title: "Investment starts at ₹2,00,000", text: "The confirmed starting investment to own a TMV Coffee & Tea franchise." },
  { icon: <Handshake size={22} className="text-gold" />, title: "Business partnership", text: "Work directly with the TMV team as a franchise partner." },
  { icon: <Truck size={22} className="text-gold" />, title: "Product distribution", text: "Coffee and tea products supplied through our wholesale network." },
  { icon: <Phone size={22} className="text-gold" />, title: "Direct enquiry channel", text: "Every franchise enquiry reaches the TMV team — by form, WhatsApp or phone." },
];

const FIELD = "field-line !text-espresso [&::placeholder]:!text-espresso/40 !border-espresso/20 focus:!border-tmvred";

const FranchisePage = () => {
  const [form, setForm] = useState({ name: "", phone: "", city: "", state: "", investment_budget: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your full name and mobile number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, enquiry_type: "franchise" });
      setDone(true);
      toast.success("Franchise enquiry submitted successfully.");
      setForm({ name: "", phone: "", city: "", state: "", investment_budget: "", message: "" });
    } catch {
      toast.error("Could not submit your enquiry. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const scrollToForm = () => {
    const el = document.querySelector("#franchise-form");
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -60, duration: 1.4 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main data-testid="franchise-page" className="bg-espresso pt-28 md:pt-36">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-20 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Link
              data-testid="franchise-back-link"
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cream/50 transition-colors hover:text-gold"
            >
              <ArrowLeft size={14} />
              Back to Home
            </Link>
            <FadeUp className="mt-8 flex items-center gap-4">
              <LogoMark className="h-12 w-12" />
              <div>
                <p className="font-display text-lg text-gold">TMV Coffee &amp; Tea</p>
                <p className="text-[10px] uppercase tracking-widest2 text-cream/50">The Modern Village</p>
              </div>
            </FadeUp>
            <h1 className="mt-8 font-display text-4xl leading-[1.15] text-cream sm:text-5xl lg:text-6xl">
              <MaskLine delay={0.1}>OWN YOUR TMV</MaskLine>
              <MaskLine delay={0.24}>
                COFFEE &amp; TEA <span className="text-gold">FRANCHISE</span>
              </MaskLine>
            </h1>
            <FadeUp delay={0.3} className="mt-7 inline-flex items-center gap-3 rounded-full border border-gold/50 bg-tmvred/20 px-6 py-3">
              <span className="font-display text-2xl text-gold">₹2,00,000</span>
              <span className="text-xs uppercase tracking-[0.15em] text-cream/70">Franchise investment starts here</span>
            </FadeUp>
            <FadeUp delay={0.38} className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="franchise-enquire-cta"
                onClick={scrollToForm}
                className="rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-espresso transition-all duration-300 hover:bg-cream"
              >
                Enquire for Franchise
              </button>
              <a
                data-testid="franchise-whatsapp-button"
                href={waLink("Hello TMV Coffee, I am interested in the ₹2,00,000 franchise opportunity. Please share details.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full border border-cream/30 px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-cream transition-all duration-300 hover:border-gold hover:text-gold"
              >
                <WhatsAppIcon className="h-4 w-4" />
                WhatsApp Us
              </a>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-sm border border-gold/40" aria-hidden="true" />
              <img
                src={IMAGES.storefront}
                alt="TMV Coffee & Tea franchise storefront"
                className="relative aspect-[4/3] w-full rounded-sm object-cover md:aspect-[16/10]"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Portfolio */}
      <section data-testid="franchise-portfolio" className="bg-cream py-20 text-espresso md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <FadeUp>
                <p className="mb-4 text-[11px] uppercase tracking-widest2 text-tmvred">Franchise Portfolio</p>
              </FadeUp>
              <h2 className="font-display text-3xl leading-tight sm:text-4xl">
                <MaskLine delay={0.05}>TMV Coffee &amp; Tea</MaskLine>
                <MaskLine delay={0.16} className="italic text-tmvred">
                  The Modern Village
                </MaskLine>
              </h2>
            </div>
            <FadeUp delay={0.2} className="max-w-sm text-sm leading-relaxed text-espresso/70">
              A franchise built on our confirmed product range and wholesale distribution network —
              starting at ₹2,00,000.
            </FadeUp>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.08}>
                <div
                  data-testid={`franchise-category-${i + 1}`}
                  className="flex h-full flex-col rounded-sm border border-espresso/10 bg-[#FAF6EE] p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(18,14,13,0.3)]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-tmvred/10 text-tmvred">
                    <c.icon size={19} />
                  </span>
                  <h3 className="mt-5 font-display text-xl">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-espresso/60">{c.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.15} className="mt-12">
            <div className="relative overflow-hidden rounded-sm">
              <img
                src={IMAGES.distribution}
                alt="TMV wholesale coffee and tea distribution"
                loading="lazy"
                className="aspect-[21/9] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-espresso/80 via-espresso/30 to-transparent" />
              <div className="absolute inset-0 flex items-center px-7 md:px-12">
                <p className="max-w-md font-display text-2xl italic leading-snug text-cream md:text-3xl">
                  Supplied through the TMV wholesale &amp; distribution network.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-tmvred py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <FadeUp>
            <p className="mb-4 text-[11px] uppercase tracking-widest2 text-gold">The Partnership</p>
          </FadeUp>
          <h2 className="mb-12 max-w-2xl font-display text-3xl leading-tight text-cream sm:text-4xl">
            <MaskLine delay={0.05}>What a TMV franchise</MaskLine>
            <MaskLine delay={0.16} className="text-gold">
              looks like.
            </MaskLine>
          </h2>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {HIGHLIGHTS.map((h, i) => (
              <FadeUp key={h.title} delay={i * 0.08}>
                <div data-testid={`franchise-highlight-${i + 1}`} className="border-t border-gold/40 pt-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/50">
                    {h.icon}
                  </div>
                  <h3 className="mt-5 font-display text-xl text-cream">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-cream/75">{h.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="franchise-form" data-testid="franchise-form-section" className="py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="mb-4 text-[11px] uppercase tracking-widest2 text-gold">Take The First Step</p>
            </FadeUp>
            <h2 className="font-display text-3xl leading-tight text-cream sm:text-4xl">
              <MaskLine delay={0.05}>Franchise</MaskLine>
              <MaskLine delay={0.16} className="italic text-gold">
                enquiry.
              </MaskLine>
            </h2>
            <FadeUp delay={0.2} className="mt-6 max-w-md text-sm leading-relaxed text-cream/65">
              Share your details and the TMV Coffee &amp; Tea team will reach out to discuss the
              franchise — investment starts at ₹2,00,000. You can also call us on{" "}
              <a href={`tel:${SITE.phone}`} data-testid="franchise-phone-link" className="text-gold underline-offset-4 hover:underline">
                {SITE.phone}
              </a>
              .
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-7">
            {done ? (
              <div
                data-testid="franchise-success-panel"
                className="flex h-full flex-col items-start justify-center rounded-sm border border-gold/40 bg-coffee/50 p-10"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sage/30 text-gold">
                  <Check size={22} />
                </span>
                <h3 className="mt-6 font-display text-2xl text-cream">Enquiry submitted successfully.</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-cream/70">
                  Thank you for your interest in a TMV Coffee &amp; Tea franchise. Our team will
                  reach out to you shortly.
                </p>
                <button
                  data-testid="franchise-another-enquiry-button"
                  onClick={() => setDone(false)}
                  className="mt-7 rounded-full border border-cream/25 px-6 py-3 text-[11px] uppercase tracking-[0.15em] text-cream/70 transition-colors hover:border-gold hover:text-gold"
                >
                  Submit another enquiry
                </button>
              </div>
            ) : (
              <form
                data-testid="franchise-enquiry-form"
                onSubmit={submit}
                className="rounded-sm border border-cream/10 bg-coffee/40 p-7 md:p-9"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <input data-testid="franchise-name-input" className={FIELD} placeholder="Full Name *" value={form.name} onChange={set("name")} />
                  <input data-testid="franchise-phone-input" className={FIELD} placeholder="Mobile Number *" type="tel" value={form.phone} onChange={set("phone")} />
                  <input data-testid="franchise-city-input" className={FIELD} placeholder="City / District" value={form.city} onChange={set("city")} />
                  <input data-testid="franchise-state-input" className={FIELD} placeholder="State" value={form.state} onChange={set("state")} />
                  <input data-testid="franchise-budget-input" className={`${FIELD} sm:col-span-2`} placeholder="Investment Budget (e.g. ₹2,00,000)" value={form.investment_budget} onChange={set("investment_budget")} />
                  <textarea data-testid="franchise-message-input" className={`${FIELD} sm:col-span-2`} rows="3" placeholder="Message (optional)" value={form.message} onChange={set("message")} />
                </div>
                <button
                  data-testid="franchise-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-8 w-full rounded-full bg-tmvred py-4 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:bg-tmvdeep disabled:opacity-50"
                >
                  {sending ? "Submitting..." : "Enquire for Franchise"}
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </section>
    </main>
  );
};

export default FranchisePage;
